/** Bits shared by the bell dropdown, the arrival toast and the detail
 *  modal. Kept in one place because all three render the same reaction
 *  strip and the same relative timestamp — they used to carry three
 *  private copies that drifted. */
import type { Notification } from "@/messaging/types";

export const QUICK_REACTIONS = ["👍", "❤️", "🎉", "👏", "🔥", "😂"];

/** Compact relative time. Deliberately separate from ``chat/time.ts``:
 *  chat needs clock times inside a day's thread, the bell only ever wants
 *  "how long ago". */
export function timeAgo(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 604800) return `${Math.round(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

/** Full timestamp for the detail modal footer. */
export function fullTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Broadcast-shaped notifications carry a shared ``announcement_id`` in
 *  their payload — owner announcements and the monthly leaderboard
 *  rollup both do. That id, not ``kind``, is what unlocks the reactions
 *  UI, so every caller reads it through here. */
export function announcementId(n: Notification): string | null {
  const id = n.payload?.announcement_id;
  return typeof id === "string" ? id : null;
}

/** ``link_url`` is author-supplied. Only same-origin paths and http(s)
 *  URLs are ever followed — a ``javascript:`` href would otherwise run
 *  for every recipient who clicked it. The backend rejects these too;
 *  this guards rows written before that validation existed. */
export function safeLink(url: string | null): string | null {
  if (!url) return null;
  if (url.startsWith("/")) return url;
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

/** Quick reactions minus the emoji already showing as a counted pill, so
 *  the same face never appears twice in one strip. */
export function unusedQuickReactions(n: Notification): string[] {
  const used = new Set(n.reactions.map((r) => r.emoji));
  return QUICK_REACTIONS.filter((e) => !used.has(e));
}

/** Day bucket for the dropdown's sticky sub-headers. */
export function dayBucket(iso: string): "Today" | "Yesterday" | "Earlier" {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = d.getTime();
  if (t >= startOfToday) return "Today";
  if (t >= startOfToday - 86_400_000) return "Yesterday";
  return "Earlier";
}
